import { PrimaryButton } from '@changegps/horizon-component-library';
import { Box, Divider } from '@mui/material';

import { ControlledInputs } from '@/components';

import {
  useCompanyProfileFormContext,
  useResetCompanyProfile,
  useUpdateCompanyProfile,
} from '../hooks';
import { CompanyProfile } from '../types';

const formFields: { name: keyof CompanyProfile; label: string }[] = [
  { name: 'company_name', label: 'Company Name' },
  { name: 'abn', label: 'ABN' },
  { name: 'phone', label: 'Phone Number' },
  { name: 'company_email', label: 'Email' },
];

const addressFields: { name: keyof CompanyProfile; label: string }[] = [
  { name: 'address1', label: 'Address Line 1' },
  { name: 'address2', label: 'Address Line 2' },
  { name: 'city', label: 'Suburb' },
  { name: 'state', label: 'State' },
  { name: 'post_code', label: 'Post Code' },
  { name: 'website', label: 'Website' },
];

export const CompanyProfileForm = () => {
  const form = useCompanyProfileFormContext();
  const { mutate: update, isPending } = useUpdateCompanyProfile();
  const resetCompanyProfile = useResetCompanyProfile();

  const handleUpdateCompanyProfile = form.handleSubmit((values) => {
    update(values, {
      onSuccess: () => {
        resetCompanyProfile();
      },
    });
  });

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          padding: '1em',
          alignItems: 'stretch', // Ensures both columns stretch
          height: '100%', // Forces full height
        }}
      >
        {/* Left Column */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 3.6,
            height: '100%',
          }}
        >
          {formFields.map((field) => (
            <Box
              sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              key={field.name}
            >
              {field.name === 'abn' ? (
                <ControlledInputs.TextFieldWithABNPattern
                  ControllerProps={{
                    control: form.control,
                    name: field.name,
                  }}
                  label={field.label}
                  fullWidth
                />
              ) : (
                <ControlledInputs.TextField
                  ControllerProps={{
                    control: form.control,
                    name: field.name,
                  }}
                  label={field.label}
                  fullWidth
                />
              )}
            </Box>
          ))}
        </Box>

        {/* Divider */}
        <Divider orientation="vertical" flexItem />

        {/* Right Column */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: '100%',
          }}
        >
          {addressFields.slice(0, 3).map((field) => (
            <Box
              sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              key={field.name}
            >
              <ControlledInputs.TextField
                ControllerProps={{
                  control: form.control,
                  name: field.name,
                }}
                label={field.label}
                fullWidth
              />
            </Box>
          ))}

          {/* State & Post Code in one row */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {addressFields.slice(3, 5).map((field) => (
              <Box sx={{ flex: 1 }} key={field.name}>
                <ControlledInputs.TextField
                  ControllerProps={{
                    control: form.control,
                    name: field.name,
                  }}
                  label={field.label}
                  fullWidth
                />
              </Box>
            ))}
          </Box>

          {/* Website Field */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <ControlledInputs.TextField
              ControllerProps={{
                control: form.control,
                name: addressFields[5].name,
              }}
              label={addressFields[5].label}
              fullWidth
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          padding: '1em',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <PrimaryButton
          disabled={isPending}
          onClick={() => handleUpdateCompanyProfile()}
        >
          {isPending ? 'Saving...' : 'Save Changes'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};
